import { wxMiniProgramEnv } from '@/modules/weChat';
import { isHasAllOwnProperty, isInvalid, isObject, isUndefined } from '@/modules/validate';
import { getBrowser } from '@/modules/utils';
import { getHost } from '@/modules/url/common';
import { loggerWarn, loggerInfo } from '@/modules/logger';

import { LogModule } from '@/data/config';
import { appType, ThsType } from '@/data/detector';
import { Detector } from '@/interface';
import {
  isAndroidRegx,
  isChrome,
  isDarkThemeRegx,
  isDingTalkRegx,
  isDZHRegx,
  isHstSDKRegx,
  isIOSRegx,
  isMACRegx,
  isMobileRegx,
  isQQRegx,
  isSafari,
  isTHSHexinGphoneRegx,
  isTHSIHexinRegx,
  isWechatRegx,
  isWeiBoRegx,
  isWINRegx,
  isWxMiniProgramRegx,
  // isWxMiniProgramRegx,
} from './regx';
import { getUtilsConfig } from '@/index';

export const getUAgent = (
  customUA?: string
): {
  UA: string;
  ua: string;
} => {
  const _UA = customUA ?? getBrowser()?.navigator?.userAgent;
  return {
    UA: _UA,
    ua: _UA?.toLowerCase(),
  };
};

/**
 * 客户端 服务端
 * 获取user-agent，默认取客户端UA
 * @param {Object} context (可选)服务端执行上下文
 */
export function getUserAgent<T = Record<any, any>>(context?: T): string {
  if (isBrowser()) {
    return getBrowser().navigator.userAgent;
  }

  /**
   * 取值优先顺序
   * 1. context.headers['user-agent']
   * 2. context.req['user-agent']
   * 3. context.req.headers['user-agent']
   * 4. ''
   */
  if (isObject(context)) {
    if (isHasAllOwnProperty(['headers'], context)) {
      const headers = context.headers as Record<string, any>;

      if (headers?.['user-agent']) {
        return headers['user-agent'];
      }
    }

    if (isHasAllOwnProperty(['req'], context)) {
      const req = context.req as Record<string, any>;
      const headers = req.headers as Record<string, any>;

      if (req?.['user-agent']) {
        return req['user-agent'];
      }

      if (headers?.['user-agent']) {
        return headers['user-agent'];
      }
    }
  }

  loggerWarn('getUserAgent 无效context.req 请检查 context.request/req', LogModule.Detector);
  return '';
}

/**
 * 通用方法获取UA参数
 * @param tester  查询标识（可忽略打小写）
 * @param customUA 自定义传入UA判断
 * @returns 找不到返回 ''
 */
export function getUaQuery(tester: string, customUA?: string): string {
  const { ua } = getUAgent(customUA);
  const _tester = tester.toLowerCase(); // 统一转化成小写
  const idx = ua.indexOf(_tester);

  if (idx < 0) {
    return '';
  }
  // ["hstong/1.4.500", "hstong", "1.4.500"]
  const re = new RegExp(`(${_tester})/([^\\s\\]]+)`, 'i');
  const arr = ua.match(re) || [];
  return arr.length === 3 ? arr[2] : '';
}

/**
 * 获取UA版本号
 * @param {string} tester 查询标识，如hstong
 * @param {string} customUA 自定义传入UA判断
 */
export function uaVersion(tester: string, customUA?: string): string {
  return getUaQuery(tester, customUA);
}

/**
 * 获取App版本号
 * @param {string} tester 查询标识，如hstong
 * @param {string} customUA 自定义传入UA判断
 */
export function appVersion(tester?: string, customUA?: string): string {
  /** 如果传入tester，直接返回 */
  if (tester) uaVersion(tester, customUA);

  const utilsConfig = getUtilsConfig();
  const AppFlag = utilsConfig.constant.APP_FLAG;

  /** 遍历获取版本号，如果有值，直接返回 */
  return AppFlag.reduce((pre, flag) => {
    if (pre) return pre;
    const _flag = flag.toLocaleLowerCase();

    const version = uaVersion(_flag, customUA);
    if (version) {
      pre = version;
    }
    return pre;
  }, '');
}

/**
 * 判断是否是Android
 * @param {string} customUA 自定义传入UA判断
 */
export function isAndroid(customUA?: string): boolean {
  const { ua } = getUAgent(customUA);
  return isAndroidRegx.test(ua);
}

/**
 * 判断是否是IOS
 * @param {string} customUA 自定义传入UA判断
 */
export function isIOS(customUA?: string): boolean {
  const { ua } = getUAgent(customUA);
  return isIOSRegx.test(ua);
}

/**
 * 判断是否是Mac端， 如果需要判断在Mac且在华盛通客户端则需加上inAPP
 * @param {string} customUA 自定义传入UA判断
 */
export function isMAC(customUA?: string): boolean {
  const { ua } = getUAgent(customUA);
  return !isMobile() && isMACRegx.test(ua);
}

/**
 * 判断是否是Win端， 如果需要判断在Win且在华盛通客户端则需加上inAPP
 * @param {string} customUA 自定义传入UA判断
 */
export function isWIN(customUA?: string): boolean {
  const { ua } = getUAgent(customUA);
  return !isMobile() && isWINRegx.test(ua);
}

/**
 * 判断是否在微博
 * @param {string} customUA 自定义传入UA判断
 */
export function isWeiBo(customUA?: string): boolean {
  const { ua } = getUAgent(customUA);
  return isWeiBoRegx.test(ua);
}

/**
 * 判断是否在QQ
 * @param {string} customUA 自定义传入UA判断
 */
export function isQQ(customUA?: string): boolean {
  const { ua } = getUAgent(customUA);
  return isQQRegx.test(ua);
}

/**
 * 判断是否在钉钉
 * @param {string} customUA 自定义传入UA判断
 */
export function isDingTalk(customUA?: string): boolean {
  const { ua } = getUAgent(customUA);
  return isDingTalkRegx.test(ua);
}

/**
 * 判断是否是WeChat平台
 * @param {string} customUA 自定义传入UA判断
 */
export function isWeChat(customUA?: string): boolean {
  const { ua } = getUAgent(customUA);

  return isWechatRegx.test(ua);
}

/**
 * 判断是否是微信小程序
 * @param {customUA} 可选
 */
export async function isWxMiniProgram(customUA?: string): Promise<boolean> {
  if (!isWeChat(customUA)) return false;
  const { ua } = getUAgent(customUA);

  // 微信7.0.0开始
  if (isWxMiniProgramRegx.test(ua)) {
    loggerInfo('userAgent中包含miniProgram', LogModule.Detector);
    return true;
  }

  if (window?.__wxjs_environment === 'miniprogram') {
    loggerInfo('window.__wxjs_environment 判断', LogModule.Detector);
    return true;
  }

  return wxMiniProgramEnv().then(data => {
    if (!data) return false;
    return data.miniprogram;
  });
}

/**
 * 是否自有開發的SDK(通用)
 * @param {string} customUA (可选)
 */
export const inHstSDK = (customUA?: string): boolean => {
  const { ua } = getUAgent(customUA);
  return isHstSDKRegx.test(ua);
};

/**
 * 判断是否嵌入在第三方App中
 * 目前用到的是新浪SDK
 * @param {appType} type
 * @param {string} customUA (可选)
 */
export const inSDKAppType = (type: appType[], customUA?: string): boolean => {
  const { ua } = getUAgent(customUA);
  if (!type.length) return false;
  return new RegExp('APPTYPE\\/(' + type.join('|') + ')', 'i').test(ua);
};

/**
 * 判断是否是新浪SDK
 */
export const inSinaFinance = (customUA?: string): boolean => {
  const { ua } = getUAgent(customUA);
  return inHstSDK(ua) ?? inSDKAppType([appType.SinaFinanceIOS, appType.SinaFinanceAndroid], ua);
};

function inAppSingle(flag: string, ua: string) {
  const _flag = flag.toLocaleLowerCase();

  const regex = new RegExp(`[[\\s]((${_flag})/([\\d.()_-]+))(?:\\]|\\s|$)`, 'i');
  const isApp = ua?.indexOf(_flag) > 0 && (ua?.match(regex) || []).length === 4;
  return isApp;
}

/**
 * 判断是不是在app
 * 默认值 options: {
 * ignoreAppType: [] // 需要忽略SDK
 * }
 */
export function inApp(options?: Detector.IInAppOptions): boolean {
  const utilsConfig = getUtilsConfig();

  const _options = options ?? {
    ignoreAppType: [],
    ignoreSDK: false,
  };
  const { ua } = getUAgent(options?.customUA);

  const isApp = utilsConfig.constant.APP_FLAG.some(flag => inAppSingle(flag, ua));

  const isHstSDK = inHstSDK(ua);

  // 忽略AppType正则
  let ignoreAppTypeRegx = null;
  if (_options && _options?.ignoreAppType?.length) {
    ignoreAppTypeRegx = inHstSDK(ua) && inSDKAppType(_options.ignoreAppType, ua);
  }

  // 判断是否忽略SDK
  if (_options.ignoreSDK) {
    return !isHstSDK && isApp;
  }

  // 判断是否针对忽略某个SDK
  return ignoreAppTypeRegx ? isApp && !ignoreAppTypeRegx : isApp;
}

// 用于桥的判断，可能不需要这个判断了
export function isIOSWithBridge(options?: Detector.IInAppOptions): boolean {
  const { ua } = getUAgent(options?.customUA);
  if (!inApp(options) || !(isIOS(ua) || isMAC(ua) || isWIN(ua))) {
    return false;
  }
  const version = appVersion('hstong', ua);

  return Boolean(version);
}

// 填充版本 操作
function fillVersion(version: string[], length: number, flag = '0') {
  const fillArr = new Array(length).fill(flag);

  return fillArr.map((i, index) => {
    return version[index] ? version[index] : i;
  });
}

/**
 * 判断前者是否大于后者
 * 当 options 没有值时，则走旧方法流程 0，-1，1
 * @param {string} version1 当前版本 appVersion 获取
 * @param {string} version2 新版本
 * @param {boolean} options[containsEqual] 判断是否包含等于
 * @param {boolean} options[isLess] 判断是否 前者 是否小于 后者
 * @returns {boolean}
 */
export function appVersionComparator(
  version1: string,
  version2: string,
  options?: {
    containsEqual?: boolean;
    isLess?: boolean;
  }
): boolean | number {
  const _options = {
    ...options,
  };
  const v1 = version1.trim() ?? '';
  const v2 = version2.trim() ?? '';
  if (!v1 || !v2) return false;

  // 判断是否是版本号
  const regex = /^(0|[1-9]\d*)\.(0|[1-9]\d*)(\.\d+)*$/;

  if (!regex.test(v1) || !regex.test(v2)) return false;

  let v1Strs = v1.split('.');
  let v2Strs = v2.split('.');

  // 判断 是不是v1 长于 v2 版本
  const isV1Long = v1Strs.length > v2Strs.length;
  const diff = Math.abs(v1Strs.length - v2Strs.length);
  const length = isV1Long ? v1Strs.length : v2Strs.length;

  if (diff > 0) {
    // 填充
    if (isV1Long) {
      v2Strs = fillVersion(v2Strs, length);
    } else {
      v1Strs = fillVersion(v1Strs, length);
    }
  }

  for (let i = 0; i < v1Strs.length; i++) {
    const v1Val = v1Strs[i];
    const v2Val = v2Strs[i];

    // 旧版逻辑判断
    if (isUndefined(options)) {
      if (v1Val < v2Val) return -1;
      if (v1Val > v2Val) return 1;
      if (i === v1Strs.length - 1 && v1Val === v2Val) {
        return false;
      }
    } else {
      if (v1Val === v2Val) continue;
      // 判断是否是小于比较
      return _options.isLess ? v1Val < v2Val : v1Val > v2Val;
    }
  }

  // 说明两个都相等
  return _options?.containsEqual ? true : false;
}

/**
 * 判断是否是移动端
 *  */
export function isMobile(customUA?: string): boolean {
  const { ua } = getUAgent(customUA);
  return isMobileRegx.test(ua);
  // return /(phone|pad|pod|iPhone|iPod|ios|iPad|Android|Mobile|BlackBerry|IEMobile|MQQBrowser|JUC|Fennec|wOSBrowser|BrowserNG|WebOS|Symbian|Windows Phone)/i.test(ua)
}

/**
 * 判断是否是PC
 *  */
export function isPC(customUA?: string): boolean {
  return !isMobile(customUA) && (isMAC(customUA) || isWIN(customUA));
}

/**
 * 获取微信的版本号
 * @returns {string}
 */
export function wechatVersion(customUA?: string): string {
  const { ua } = getUAgent(customUA);
  if (!isWeChat(ua)) {
    return '';
  }
  const re = new RegExp('\\micromessenger\\/([\\d\\.]+)(?:\\(|\\s|$)', 'i');
  const arr = ua.match(re) || [];
  return arr.length === 2 ? arr[1] : '';
}

/**
 * 判断安卓微信版本是否需要弹出照相机
 *  */
export function isWxInAndroidAndNeedCaptureCamera(
  compareVersion = '7.0.13',
  customUA?: string
): boolean {
  const { ua } = getUAgent(customUA);
  if (!isAndroid(ua)) return false;
  const wechat = wechatVersion(ua);
  if (!wechat) {
    return false;
  }
  const v = wechat.split('(')[0];
  return appVersionComparator(v, compareVersion) >= 0;
}

/**
 * 判断是否是大智慧
 */
export function isDZH(customUA?: string): boolean {
  const { ua } = getUAgent(customUA);
  return isDZHRegx.test(ua);
}

/**
 * 判断同花顺
 */
export function isTHSHexinGphone(customUA?: string): boolean {
  const { ua } = getUAgent(customUA);
  return isTHSHexinGphoneRegx.test(ua);
}
export function isTHSIHexin(customUA?: string): boolean {
  const { ua } = getUAgent(customUA);
  return isTHSIHexinRegx.test(ua);
}

/**
 * 判断是否是同花顺
 */
export function isTHS(options?: { type?: ThsType; customUA?: string }): boolean {
  const { ua } = getUAgent(options?.customUA);
  if (options?.type === ThsType.Hexin_Gphone) return isTHSHexinGphone(ua);
  if (options?.type === ThsType.IHexin) return isTHSIHexin(ua);
  return isTHSHexinGphone(ua) || isTHSIHexin(ua);
}

/**
 * 获取同花顺APP版本号
 */
export function getTHSAppVersion(customUA?: string): string {
  const { ua } = getUAgent(customUA);
  if (isTHSHexinGphone(ua)) {
    const a = ua.match(/Hexin_Gphone\/\S*/i);
    if (!a?.length) return '';
    return a[0].replace(/Hexin_Gphone\//gi, '');
  }

  if (isTHSIHexin(ua)) {
    const a = ua.match(/IHexin\/\S*/i);
    if (!a?.length) return '';
    return a[0].replace(/IHexin\//gi, '');
  }

  return '';
}

/**
 * 通过user agent判断是否是暗黑模式
 */
export function isDarkTheme(customUA?: string): boolean {
  const { ua } = getUAgent(customUA);
  return isDarkThemeRegx.test(ua);
}

/**
 * 判断是否是浏览器
 * 支持 浏览器，服务端
 */
export function isBrowser() {
  try {
    const global = getGlobal();
    return !isInvalid(global) && global === global?.window;
  } catch {
    return false;
  }
}

/**
 * 获取全局对象
 * 支持 浏览器，服务端
 * 查找顺序 globalThis window self
 */
export function getGlobal() {
  try {
    if (!isUndefined(globalThis)) return globalThis;
  } catch (e) {
    loggerWarn('请忽略 - globalThis 找不到', LogModule.Detector);
  }
  try {
    if (!isUndefined(window)) return window;
  } catch (e) {
    loggerWarn('请忽略 - window 找不到', LogModule.Detector);
  }
  try {
    if (!isUndefined(self)) return self;
  } catch (e) {
    throw new Error('unable to locate global object');
  }
}

/**
 * 判断当前域名是否是hstong.com
 * @returns
 */
export function isHstong(url?: string) {
  const _url = url ?? (isBrowser() ? getBrowser().location.hostname : '');
  if (!_url) return;

  const host = getHost(_url);
  if (!host) return;

  return /hstong/i.test(host);
}

/**
 * 判断是否是香港域名
 * @param url
 * @returns
 */
export function isHKDomain(url?: string) {
  const _url = url ?? (isBrowser() ? getBrowser().location.href : '');
  if (!_url) return;

  const host = getHost(_url);
  if (!host) return;

  return /^hk/i.test(host);
}

/**
 * 判断是否微信开发者工具
 */
export function isWeChatDevTools(customUA?: string): boolean {
  const { ua } = getUAgent(customUA);
  const _isWeChat = isWeChat(ua);
  if (!_isWeChat) return false;

  return /wechatdevtools/i.test(ua);
}

/**
 * 判断是否在 Safari 游览器
 * @param {string} customUA 自定义传入UA判断
 */
export function isSafariBrowser(customUA?: string): boolean {
  const { ua } = getUAgent(customUA);
  return isSafari.test(ua) && !isChrome.test(ua);
}

/**
 * @isBlob
 * @desc 判断是否是Blob、文件
 * @param value
 */
export const isBlob = function isBlob(value: any) {
  if (isInvalid(value) || isInvalid(Blob)) return false;
  return value instanceof Blob || Object.prototype.toString.call(value) === '[object Blob]';
};

/**
 * 判断是否是IP
 */
export const isIP = function (value: string) {
  return /([1-9]?\d|1\d{2}|2[0-4]\d|25[0-5])(\.([1-9]?\d|1\d{2}|2[0-4]\d|25[0-5])){3}$/.test(value);
};
